import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { Box, useTheme } from "@mui/material";
import { COMMAND_PRIORITY_HIGH, FORMAT_TEXT_COMMAND } from "lexical";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";

import { InsertTextAtCursorPlugin } from "./insertTextAtCursor";
import { $serializeEditorToPlainText } from "./lexicalTextSync";
import { useLexicalPlainTextSync } from "./useLexicalPlainTextSync";
import { useMessageDraftExtensions } from "../editor/messageDraftStoreFactory";

export interface LexicalEditorContentProps {
  initialText: string;
  onTextChange: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  onCloseEmojiPicker?: () => void;
}

function EditorSyncPlugin({
  initialText,
  onTextChange,
}: {
  initialText: string;
  onTextChange: (text: string) => void;
}) {
  const handleChange = useLexicalPlainTextSync({
    value: initialText,
    onChange: onTextChange,
  });

  return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />;
}

function EditablePlugin({ disabled }: { disabled: boolean }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  return null;
}

/**
 * Rich text so multi-line paste lands as one paragraph per line (the shape the
 * product-marker rebuild scans) — but the message is still plain text on the
 * wire, so keyboard formatting (cmd+B, …) is swallowed until the composer
 * actually ships rich content. Formatting arriving via HTML paste is visual
 * only and drops on reload; the serializer reads text content.
 */
function DisableTextFormattingPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      FORMAT_TEXT_COMMAND,
      () => true,
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return null;
}

/**
 * Lexical keeps a paragraph (and often a `<br>`) inside the contenteditable,
 * so the `:empty` CSS selector never matches and the default placeholder
 * would stay hidden. Mirror emptiness from serialized text instead.
 */
function PlaceholderSyncPlugin({
  onEmptyChange,
}: {
  onEmptyChange: (empty: boolean) => void;
}) {
  const [editor] = useLexicalComposerContext();

  const readEmpty = useCallback(() => {
    editor.getEditorState().read(() => {
      onEmptyChange($serializeEditorToPlainText().trim() === "");
    });
  }, [editor, onEmptyChange]);

  useLayoutEffect(() => {
    readEmpty();
    return editor.registerUpdateListener(() => {
      readEmpty();
    });
  }, [editor, readEmpty]);

  return null;
}

function ExtensionPlugins() {
  const extensions = useMessageDraftExtensions();
  const plugins = extensions.flatMap((ext) => ext.editor?.plugins ?? []);
  return (
    <>
      {plugins.map((Plugin, index) => (
        <Plugin key={index} />
      ))}
    </>
  );
}

/**
 * The Lexical editor content area. Must be rendered inside a
 * `<LexicalComposer>` so that merge tag insertion and emoji commands
 * can share the same editor instance.
 *
 * The editor is fully extension-driven: nodes are registered at the
 * composer level (see {@link useExtensionLexicalNodes}), and plugins are
 * rendered here from the active extensions.
 */
export function LexicalEditorContent({
  initialText,
  onTextChange,
  disabled = false,
  placeholder = "Type your message",
}: LexicalEditorContentProps) {
  const theme = useTheme();
  const [isEmpty, setIsEmpty] = useState(() => initialText.trim().length === 0);
  const handleEmptyChange = useCallback((empty: boolean) => {
    setIsEmpty(empty);
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        "& .lexical-paragraph": {
          margin: 0,
          padding: 0,
        },
        // A context drops whole when its model has no items, taking its blocks
        // with it, so the nesting has to read at a glance — but in quiet ink:
        // one neutral hue, solid vs dotted rails, so the copy stays darkest.
        "& .lexical-product-context": {
          margin: `${theme.spacing(2)} 0`,
          padding: `${theme.spacing(1)} 0 ${theme.spacing(1)} ${theme.spacing(3)}`,
          borderLeft: `1px solid ${theme.palette.divider}`,
          transition: "border-color 120ms",
        },
        // Hover tooltips on product chrome (`data-tip`), styled like MUI's so
        // they show at once rather than after the native title delay.
        "& [data-tip]": { position: "relative" },
        "& [data-tip]:hover::after": {
          content: "attr(data-tip)",
          position: "absolute",
          top: "calc(100% + 6px)",
          left: 0,
          zIndex: 1,
          width: "max-content",
          maxWidth: 280,
          whiteSpace: "normal",
          backgroundColor: theme.palette.grey[700],
          color: theme.palette.common.white,
          fontSize: "11px",
          lineHeight: "14px",
          fontWeight: theme.typography.fontWeightMedium,
          padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
          borderRadius: "4px",
          pointerEvents: "none",
        },
        // Header row holding the model pill (unmanaged chrome).
        "& .lexical-product-context-row": {
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(3),
          marginBottom: theme.spacing(1),
        },
        // A small pill, so "this opens a menu" reads without color.
        "& .lexical-product-context-header": {
          display: "inline-block",
          cursor: "pointer",
          userSelect: "none",
          color: theme.palette.text.secondary,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "10px",
          padding: `0 ${theme.spacing(2)}`,
          fontSize: theme.typography.caption.fontSize,
          lineHeight: "18px",
          transition: "color 120ms, border-color 120ms",
          "& .lexical-product-header-label::after": { content: '" ▾"' },
        },
        // Product blocks are dropped whole when the tile has fewer products,
        // so the boundary has to be visible while authoring.
        "& .lexical-product-block": {
          position: "relative",
          margin: `${theme.spacing(2)} 0`,
          padding: `${theme.spacing(1)} 0 ${theme.spacing(1)} ${theme.spacing(3)}`,
          borderLeft: `1px dotted ${theme.palette.divider}`,
          transition: "border-color 120ms",
        },
        // A plain label (unlike the context header's menu affordance) — block
        // indices are positional, so there is nothing to configure.
        "& .lexical-product-block-header": {
          display: "block",
          userSelect: "none",
          color: theme.palette.text.disabled,
          fontSize: theme.typography.caption.fontSize,
          lineHeight: 1.6,
          transition: "color 120ms",
        },
        // Reveal on engagement: the rails stay quiet at rest and answer
        // attention — hover, or the caret sitting inside (the plugin mirrors
        // that as `.lexical-product-engaged`). Context = secondary hue,
        // block = primary, so the old color vocabulary survives as feedback.
        "& .lexical-product-context:hover, & .lexical-product-context.lexical-product-engaged":
          {
            borderLeftColor: theme.palette.secondary.main,
          },
        "& .lexical-product-context:hover .lexical-product-context-header, & .lexical-product-context.lexical-product-engaged .lexical-product-context-header":
          {
            color: theme.palette.secondary.main,
            borderColor: theme.palette.secondary.light,
          },
        // Engaging the context hints where its blocks sit; a block the caret
        // or pointer is actually in gets the full-strength boundary below.
        "& .lexical-product-context:hover .lexical-product-block:not(:hover):not(.lexical-product-engaged), & .lexical-product-context.lexical-product-engaged .lexical-product-block:not(:hover):not(.lexical-product-engaged)":
          {
            borderLeftColor: theme.palette.primary.light,
          },
        "& .lexical-product-block:hover, & .lexical-product-block.lexical-product-engaged":
          {
            borderLeftColor: theme.palette.primary.main,
            borderLeftStyle: "solid",
          },
        "& .lexical-product-block:hover > .lexical-product-block-header, & .lexical-product-block.lexical-product-engaged > .lexical-product-block-header":
          {
            color: theme.palette.primary.main,
          },
        // Hover ✕ on either header deletes the wrapper; revealed on section
        // hover so it's discoverable without cluttering the resting state.
        "& .lexical-product-delete": {
          visibility: "hidden",
          cursor: "pointer",
          marginLeft: theme.spacing(2),
          color: theme.palette.text.secondary,
          "&:hover": { color: theme.palette.error.main },
        },
        "& .lexical-product-context-header:hover .lexical-product-delete, & .lexical-product-block-header:hover .lexical-product-delete":
          {
            visibility: "visible",
          },
        // Discover's read-only sets 2–5: dashed rails under the authored set,
        // shown only by the mirror plugin (a lone discover group), collapsed
        // until the row is opened.
        "& .lexical-product-mirrors": { display: "none" },
        "& .lexical-product-mirrors.lexical-product-mirrors-on": {
          display: "block",
          marginTop: theme.spacing(2),
        },
        "& .lexical-product-mirror": {
          margin: `${theme.spacing(2)} 0`,
          padding: `0 0 0 ${theme.spacing(3)}`,
          borderLeft: `1px dashed ${theme.palette.divider}`,
        },
        "& .lexical-product-mirror-header": {
          display: "inline-flex",
          alignItems: "center",
          gap: theme.spacing(2),
          cursor: "pointer",
          userSelect: "none",
          color: theme.palette.text.secondary,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "10px",
          padding: `0 ${theme.spacing(2)}`,
          fontSize: theme.typography.caption.fontSize,
          lineHeight: "18px",
        },
        "& .lexical-product-mirror-body": { display: "none" },
        "& .lexical-product-mirror-open > .lexical-product-mirror-body": {
          display: "block",
          opacity: 0.55,
          pointerEvents: "none",
        },
        "& .lexical-product-mirror-body .lexical-product-block": {
          borderLeftColor: theme.palette.divider,
        },
        // The two-step delete's "are you sure": the wrapper Backspace selected.
        "& .lexical-product-context.lexical-structure-selected, & .lexical-product-block.lexical-structure-selected":
          {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: "2px",
            borderRadius: "2px",
          },
        // ='true' so non-editable chrome inside the editor (the product block
        // header) doesn't inherit the editor's own box.
        "& [contenteditable='true']": {
          outline: "none",
          padding: `${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(4)}`,
          color: theme.palette.text.primary,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          minHeight: "100px",
          fontSize: theme.typography.body1.fontSize,
          lineHeight: theme.typography.body1.lineHeight,
          fontFamily: theme.typography.fontFamily,
        },
        "& .lexical-placeholder": {
          display: isEmpty ? "block" : "none",
          color: theme.palette.text.secondary,
          opacity: 0.4,
          position: "absolute",
          top: theme.spacing(2),
          left: theme.spacing(3),
          pointerEvents: "none",
          fontSize: theme.typography.body1.fontSize,
          lineHeight: theme.typography.body1.lineHeight,
          fontFamily: theme.typography.fontFamily,
        },
      }}
    >
      <RichTextPlugin
        contentEditable={<ContentEditable aria-label="Message editor" />}
        placeholder={<div className="lexical-placeholder">{placeholder}</div>}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <DisableTextFormattingPlugin />
      <HistoryPlugin />
      <InsertTextAtCursorPlugin />
      <ExtensionPlugins />
      <EditorSyncPlugin initialText={initialText} onTextChange={onTextChange} />
      <PlaceholderSyncPlugin onEmptyChange={handleEmptyChange} />
      <EditablePlugin disabled={disabled} />
    </Box>
  );
}
